import { useEffect, useMemo, useState } from "react";

import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Loader2,
  PackageCheck,
  Phone,
  ReceiptText,
  ShoppingBag,
  Timer,
  Truck,
  UserRound,
  UtensilsCrossed,
  X,
  XCircle,
} from "lucide-react";

import { Link, useParams } from "react-router-dom";

import { publicMenuApi } from "../../api";

import { useLanguage } from "../../contexts/LanguageContext";

import "../../styles/PublicMenu/public-customer-order-details.css";

const PUBLIC_MENU_ORDERS_PREFIX = "public_menu_orders_";

const DEFAULT_PUBLIC_MENU_THEME = {
  theme: "light",
  primary_color: "#51766E",
  background_color: "#F8FAF9",
  text_color: "#333333",
  surface_color: "#FFFFFF",
  accent_color: "#D7E3E0",
  border_radius: "large",
  card_style: "elevated",
  card_shadow: "soft",
  font_family: "system",
};

const hexToRgb = (color, fallback) => {
  if (!color) return fallback;
  const trimmed = String(color).trim();
  if (trimmed.startsWith("rgb")) {
    const matches = trimmed.match(/\d+(?:\.\d+)?/g);
    if (matches?.length >= 3) return matches.slice(0, 3).join(", ");
  }
  const normalized = trimmed.replace("#", "");
  const fullHex =
    normalized.length === 3
      ? normalized.split("").map((char) => `${char}${char}`).join("")
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) return fallback;
  return `${parseInt(fullHex.slice(0, 2), 16)}, ${parseInt(fullHex.slice(2, 4), 16)}, ${parseInt(fullHex.slice(4, 6), 16)}`;
};

const buildCustomerOrderTheme = (menuData) => {
  const menu = menuData?.menu || {};
  const establishment = menuData?.establishment || {};
  const template = menuData?.template || menu?.template || menu?.menu_template || {};
  return {
    ...DEFAULT_PUBLIC_MENU_THEME,
    ...(template?.config || {}),
    ...(establishment?.menu_design_settings || {}),
    ...(menuData?.design_settings || {}),
    ...(menu?.design_settings || {}),
    ...(menu?.menu_design_settings || {}),
  };
};

const STATUS_ICONS = {
  pending: Clock3,
  accepted: CheckCircle2,
  preparing: Timer,
  ready: PackageCheck,
  delivered: Truck,
  cancelled: XCircle,
};

const STATUS_STEPS = ["pending", "accepted", "preparing", "ready", "delivered"];

const ORDER_STATUS_KEYS = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "delivered",
  "cancelled",
];

const ORDER_TYPE_KEYS = ["dine_in", "takeaway", "delivery"];

const normalizeOrderStatus = (status) => {
  const value = String(status || "").toLowerCase();
  return ORDER_STATUS_KEYS.includes(value) ? value : "pending";
};

const normalizeOrderType = (type) => {
  const value = String(type || "").toLowerCase();
  return ORDER_TYPE_KEYS.includes(value) ? value : "takeaway";
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const formatPrice = (value, currency = "MAD") => {
  const amount = Number(value || 0);
  return `${amount.toFixed(2)} ${currency}`;
};

const formatOrderDate = (dateValue, language) => {
  if (!dateValue) return "-";
  try {
    return new Intl.DateTimeFormat(language || undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateValue));
  } catch {
    return "-";
  }
};

const getOrderItems = (order) => {
  return toArray(order?.items || order?.order_items || order?.products || []);
};

const getOrderItemProductId = (item) => {
  return (
    item?.product_id ||
    item?.product?.id ||
    item?.product?.slug ||
    item?.product_slug ||
    item?.product_snapshot?.product_id ||
    item?.product_snapshot?.id ||
    ""
  );
};

const getOrderItemName = (item, t) => {
  return (
    item?.product_name ||
    item?.product_snapshot?.name ||
    item?.product?.name ||
    item?.name ||
    t("products.noName")
  );
};

const mergeOrder = (currentOrder, updatedOrder) => {
  if (!currentOrder) return updatedOrder;
  return {
    ...currentOrder,
    ...updatedOrder,
    status: normalizeOrderStatus(updatedOrder?.status || currentOrder.status),
    items: updatedOrder?.items?.length ? updatedOrder.items : currentOrder.items || [],
  };
};

function CustomerOrderAlert({ alert, onClose }) {
  if (!alert) return null;
  return (
    <div className={`public-customer-order-alert public-customer-order-alert--${alert.type}`}>
      <div>
        <ReceiptText size={18} />
        <span>{alert.text}</span>
      </div>
      <button type="button" onClick={onClose} aria-label={alert.closeLabel || "Close"}>
        <X size={16} />
      </button>
    </div>
  );
}

function PublicCustomerOrderDetails() {
  const { establishmentSlug, menuSlug, orderId } = useParams();
  const menuStorageId = `${establishmentSlug || ""}__${menuSlug || ""}`;
  const publicMenuPath = `/${encodeURIComponent(establishmentSlug || "")}/${encodeURIComponent(menuSlug || "")}`;
  const { t, language } = useLanguage();
  const ordersStorageKey = `${PUBLIC_MENU_ORDERS_PREFIX}${menuStorageId}`;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [customerOrderTheme, setCustomerOrderTheme] = useState(DEFAULT_PUBLIC_MENU_THEME);
  const customerOrderThemeStyle = useMemo(
    () => ({
      "--public-menu-primary": customerOrderTheme.primary_color || "#51766E",
      "--public-menu-bg": customerOrderTheme.background_color || "#F8FAF9",
      "--public-menu-text": customerOrderTheme.text_color || "#333333",
      "--public-menu-surface": customerOrderTheme.surface_color || "#FFFFFF",
      "--public-menu-accent": customerOrderTheme.accent_color || "#D7E3E0",
      "--public-menu-primary-rgb": hexToRgb(customerOrderTheme.primary_color, "81, 118, 110"),
      "--public-menu-bg-rgb": hexToRgb(customerOrderTheme.background_color, "248, 250, 249"),
      "--public-menu-text-rgb": hexToRgb(customerOrderTheme.text_color, "51, 51, 51"),
      "--public-menu-surface-rgb": hexToRgb(customerOrderTheme.surface_color, "255, 255, 255"),
      "--public-menu-accent-rgb": hexToRgb(customerOrderTheme.accent_color, "215, 227, 224"),
    }),
    [customerOrderTheme],
  );
  const customerOrderThemeClassName = [
    "public-customer-order-page",
    `public-customer-order-page--theme-${customerOrderTheme.theme || "light"}`,
    `public-customer-order-page--radius-${customerOrderTheme.border_radius || "large"}`,
    `public-customer-order-page--card-${customerOrderTheme.card_style || "elevated"}`,
    `public-customer-order-page--shadow-${customerOrderTheme.card_shadow || "soft"}`,
    `public-customer-order-page--font-${customerOrderTheme.font_family || "system"}`,
  ].join(" ");
  const safeStatus = normalizeOrderStatus(order?.status);
  const safeType = normalizeOrderType(order?.order_type);
  const currentStatusIndex = STATUS_STEPS.indexOf(safeStatus);
  const StatusIcon = STATUS_ICONS[safeStatus] || Clock3;
  const orderItems = useMemo(() => getOrderItems(order), [order]);
  const getLocalOrders = () => {
    try {
      return JSON.parse(localStorage.getItem(ordersStorageKey) || "[]");
    } catch {
      return [];
    }
  };
  const saveLocalOrder = (nextOrder) => {
    if (!nextOrder?.id && !nextOrder?.order_number) return;
    try {
      const currentOrders = getLocalOrders();
      const orderKey = String(nextOrder.id || "");
      const orderNumber = String(nextOrder.order_number || "");
      let exists = false;
      const nextOrders = currentOrders.map((currentOrder) => {
        const sameId = orderKey && String(currentOrder.id || "") === orderKey;
        const sameNumber =
          orderNumber && String(currentOrder.order_number || "") === orderNumber;
        if (!sameId && !sameNumber) return currentOrder;
        exists = true;
        return mergeOrder(currentOrder, nextOrder);
      });
      localStorage.setItem(
        ordersStorageKey,
        JSON.stringify(exists ? nextOrders : [nextOrder, ...nextOrders]),
      );
    } catch {
      // Ignore local storage failures. The UI can still refresh from the API.
    }
  };
  const showAlert = (type, text) => {
    setAlert({ type, text });
  };
  const fetchOrder = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await publicMenuApi.getCustomerOrder(establishmentSlug, menuSlug, orderId);
      const responseData = response.data || {};
      const remoteOrder =
        responseData?.data?.order ||
        responseData?.order ||
        responseData?.data ||
        null;
      const nextOrder = remoteOrder;
      setOrder(nextOrder);
      saveLocalOrder(nextOrder);
    } catch {
      // API authorization is authoritative for customer data.
      setOrder(null);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };
  useEffect(() => {
    fetchOrder();
  }, [establishmentSlug, menuSlug, orderId, ordersStorageKey]);
  useEffect(() => {
    let cancelled = false;
    const fetchMenuTheme = async () => {
      try {
        const response = await publicMenuApi.getMenu(establishmentSlug, menuSlug);
        const menuData = response.data?.data || null;
        if (!cancelled && menuData) {
          setCustomerOrderTheme(buildCustomerOrderTheme(menuData));
        }
      } catch {
        if (!cancelled) {
          setCustomerOrderTheme(DEFAULT_PUBLIC_MENU_THEME);
        }
      }
    };
    fetchMenuTheme();
    return () => {
      cancelled = true;
    };
  }, [establishmentSlug, menuSlug]);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchOrder({ silent: true });
    }, 5000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [establishmentSlug, menuSlug, orderId, ordersStorageKey]);
  useEffect(() => {
    if (!alert) return undefined;
    const timeoutId = window.setTimeout(() => {
      setAlert(null);
    }, 4500);
    return () => window.clearTimeout(timeoutId);
  }, [alert]);
  if (loading) {
    return (
      <main className={`${customerOrderThemeClassName} public-customer-order-page--loading`} style={customerOrderThemeStyle}>
        <div className="public-customer-order-loading">
          <Loader2 size={34} />
          <h1>{t("publicMenu.customerOrder.loading")}</h1>
        </div>
      </main>
    );
  }
  if (!order) {
    return (
      <main className={customerOrderThemeClassName} style={customerOrderThemeStyle}>
        <section className="public-customer-order-shell">
          <div className="public-customer-order-empty">
            <ShoppingBag size={36} />
            <strong>{t("publicMenu.customerOrder.notFound")}</strong>
          </div>
        </section>
      </main>
    );
  }
  return (
    <main className={customerOrderThemeClassName} style={customerOrderThemeStyle}>
      <section className="public-customer-order-shell">
<header className={`public-customer-order-hero public-customer-order-hero--${safeStatus}`}>
          <div>
            <p>{t("publicMenu.customerOrder.eyebrow")}</p>
            <h1>{order.order_number || `#${order.id || orderId}`}</h1>
            <span>
              {order.customer_name || t("publicMenu.anonymous")} · {order.customer_phone || "-"}
            </span>
          </div>
          <span className="public-customer-order-hero__icon">
            <StatusIcon size={34} />
          </span>
        </header>
        <section className="public-customer-order-status-card">
          <div className="public-customer-order-status-head">
            <span
              className={`public-customer-order-status-badge public-customer-order-status-badge--${safeStatus}`}
            >
              <StatusIcon size={17} />
              {t(`orders.status.${safeStatus}`)}
            </span>
            <small>{formatOrderDate(order.updated_at || order.created_at, language)}</small>
          </div>
          {safeStatus === "cancelled" ? (
            <p className="public-customer-order-cancelled">
              {t("publicMenu.customerOrder.cancelled")}
            </p>
          ) : (
            <div className="public-customer-order-steps">
              {STATUS_STEPS.map((status, index) => {
                const StepIcon = STATUS_ICONS[status] || Clock3;
                const isActive = index <= currentStatusIndex;
                const isCurrent = index === currentStatusIndex;
                return (
                  <div
                    key={status}
                    className={`${isActive ? "active" : ""} ${
                      isCurrent ? "current" : ""
                    }`}
                  >
                    <span>
                      <StepIcon size={15} />
                    </span>
                    <strong>{t(`orders.status.${status}`)}</strong>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        <section className="public-customer-order-info-grid">
          <article>
            <span>
              <ReceiptText size={16} />
              {t("orders.table.type")}
            </span>
            <strong>{t(`orders.types.${safeType}`)}</strong>
          </article>
          <article>
            <span>
              <ShoppingBag size={16} />
              {t("orders.table.total")}
            </span>
            <strong>{formatPrice(order.total_amount, order.currency || "MAD")}</strong>
          </article>
          <article>
            <span>
              <CalendarDays size={16} />
              {t("orders.table.date")}
            </span>
            <strong>{formatOrderDate(order.created_at, language)}</strong>
          </article>
          <article>
            <span>
              <UserRound size={16} />
              {t("orders.table.customer")}
            </span>
            <strong>{order.customer_name || t("publicMenu.anonymous")}</strong>
          </article>
          <article>
            <span>
              <Phone size={16} />
              {t("orders.table.phone")}
            </span>
            <strong>{order.customer_phone || "-"}</strong>
          </article>
        </section>
        <section className="public-customer-order-card">
          <div className="public-customer-order-card__title">
            <span>
              <UtensilsCrossed size={20} />
            </span>
            <div>
              <h2>{t("orders.details.itemsTitle")}</h2>
              <p>{t("publicMenu.checkout.summaryHint")}</p>
            </div>
          </div>
          <div className="public-customer-order-items">
            {orderItems.map((item, index) => {
              const productId = getOrderItemProductId(item);
              const itemName = getOrderItemName(item, t);
              return (
                <article key={item.id || `${productId || "item"}-${index}`}>
                  <div className="public-customer-order-item__main">
                    <span>
                      <UtensilsCrossed size={18} />
                    </span>
                    <div>
                      {productId ? (
                        <Link to={`${publicMenuPath}/products/${encodeURIComponent(String(productId))}`}>
                          {itemName}
                        </Link>
                      ) : (
                        <strong>{itemName}</strong>
                      )}
                      {(item.size_name || item.product_snapshot?.size_name) && (
                        <small>
                          {item.size_name || item.product_snapshot?.size_name}
                        </small>
                      )}
                      {toArray(item.supplements_snapshot || item.product_snapshot?.supplements)
                        .length > 0 && (
                        <small>
                          {toArray(
                            item.supplements_snapshot || item.product_snapshot?.supplements,
                          )
                            .map((supplement) => supplement?.name || supplement)
                            .filter(Boolean)
                            .join(", ")}
                        </small>
                      )}
                    </div>
                  </div>
                  <span className="public-customer-order-item__quantity">
                    ×{item.quantity || 1}
                  </span>
                  <strong className="public-customer-order-item__price">
                    {formatPrice(item.total_price, order.currency || "MAD")}
                  </strong>
                  {productId && (
                    <Link
                      to={`${publicMenuPath}/products/${encodeURIComponent(String(productId))}`}
                      className="public-customer-order-item__open"
                      aria-label={`${t("publicMenu.customerOrders.openProductDetails")}: ${itemName}`}
                    >
                      <ChevronRight size={18} />
                    </Link>
                  )}
                </article>
              );
            })}
            {orderItems.length === 0 && (
              <div className="public-customer-order-empty public-customer-order-empty--inside">
                <ShoppingBag size={32} />
                <strong>{t("orders.details.noItems")}</strong>
              </div>
            )}
          </div>
        </section>
      </section>
      <CustomerOrderAlert alert={alert} onClose={() => setAlert(null)} />
    </main>
  );
}

export default PublicCustomerOrderDetails;
